import React, { useState, useEffect, useCallback } from "react";
import {
    FolderKanban,
    Users,
    Users2,
    Plus,
    ArrowLeft,
    Loader2,
    AlertCircle,
    Trash2,
    MoreVertical,
    Edit2
} from "lucide-react";
import {
    fetchGroups,
    fetchWorkspaceMembers,
    createGroup,
    deleteGroup
} from "../../api/organizations";
import { isLimitReached, getWorkspaceColor } from "../../utils/organizationHelpers";
import CreateEntityModal from "./CreateEntityModal";
import MembersList from "./MembersList";

const GroupCard = ({ group, index, onSelect, onDelete, canEdit = false }) => {
    const [showMenu, setShowMenu] = useState(false);

    return (
        <div
            className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-xl p-4 hover:border-purple-500/50 dark:hover:border-purple-500/50 transition-all cursor-pointer group relative"
            onClick={() => onSelect(group)}
        >
            {/* Menu Button - Only for admins/owners */}
            {canEdit && (
                <div className="absolute top-3 right-3">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                        }}
                        className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>
                    {showMenu && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(false);
                                }}
                            />
                            <div className="absolute right-0 mt-1 w-36 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg shadow-xl z-20 py-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(group);
                                        setShowMenu(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                    <Users2 className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h4 className="dark:text-white text-slate-900 font-medium">{group.name}</h4>
                    <p className="text-xs dark:text-slate-400 text-slate-500">
                        {group.memberCount || 0} members
                    </p>
                </div>
            </div>
            {group.description && (
                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2 line-clamp-2">
                    {group.description}
                </p>
            )}
        </div>
    );
};

const WorkspaceDetail = ({
    organization,
    workspace,
    onGroupSelect,
    onBack,
    limits,
    userEmail,
    canEdit = false
}) => {
    const [groups, setGroups] = useState([]);
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeSection, setActiveSection] = useState("groups");

    // Create modal
    const [createModalOpen, setCreateModalOpen] = useState(false);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const canCreateGroup = canEdit && !isLimitReached(groups.length, limits.groupsPerWorkspace);

    const loadData = useCallback(async () => {
        // Handle both id and workspace_id from backend
        const orgId = organization?.id || organization?.org_id;
        const wsId = workspace?.id || workspace?.workspace_id;
        if (!orgId || !wsId) return;

        setIsLoading(true);
        setError("");
        try {
            const [groupsRes, membersRes] = await Promise.all([
                fetchGroups(orgId, wsId).catch(() => ({ groups: [] })),
                fetchWorkspaceMembers(orgId, wsId).catch(() => ({ members: [] }))
            ]);
            // Handle various response structures and normalize IDs
            const groupsData = groupsRes?.groups || groupsRes?.data?.groups || [];
            const membersData = membersRes?.members || membersRes?.data?.members || [];
            // Normalize group_id to id
            const normalizedGroups = (Array.isArray(groupsData) ? groupsData : []).map(g => ({
                ...g,
                id: g.group_id || g.id
            }));
            setGroups(normalizedGroups);
            setMembers(Array.isArray(membersData) ? membersData : []);
        } catch (err) {
            console.error("Failed to load workspace data:", err);
            setError("Unable to load workspace data.");
        } finally {
            setIsLoading(false);
        }
    }, [organization?.id, organization?.org_id, workspace?.id, workspace?.workspace_id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleCreateGroup = async (data) => {
        try {
            const orgId = organization?.id || organization?.org_id;
            const wsId = workspace?.id || workspace?.workspace_id;
            // Transform form data to match API spec
            const payload = {
                name: data.name,
                description: data.description || "",
                settings: {}
            };
            await createGroup(orgId, wsId, payload);
            setCreateModalOpen(false);
            await loadData(); // Await refresh
        } catch (err) {
            throw err;
        }
    };

    const handleDeleteGroup = async () => {
        if (!groupToDelete) return;
        setIsDeleting(true);
        setError("");
        try {
            const orgId = organization?.id || organization?.org_id;
            const wsId = workspace?.id || workspace?.workspace_id;
            const groupId = groupToDelete.group_id || groupToDelete.id;
            await deleteGroup(orgId, wsId, groupId);
            setDeleteModalOpen(false);
            setGroupToDelete(null);
            await loadData(); // Await refresh
        } catch (err) {
            setError(err.message || "Failed to delete group");
        } finally {
            setIsDeleting(false);
        }
    };

    const openDeleteModal = (group) => {
        setGroupToDelete(group);
        setDeleteModalOpen(true);
    };

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-auto">
            {/* Header */}
            <div className="px-6 py-6 border-b dark:border-slate-800 border-slate-200">
                <div className="max-w-5xl mx-auto">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500 hover:text-teal-500 dark:hover:text-teal-400 mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Workspaces
                    </button>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getWorkspaceColor(0)} flex items-center justify-center`}>
                                <FolderKanban className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold dark:text-white text-slate-900">
                                    {workspace.name}
                                </h2>
                                {workspace.description && (
                                    <p className="dark:text-slate-400 text-slate-500 mt-1">
                                        {workspace.description}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <span className="px-3 py-1.5 dark:bg-slate-800 bg-slate-100 rounded-lg dark:text-slate-300 text-slate-600">
                                <Users className="w-4 h-4 inline mr-1.5" />
                                {members.length} members
                            </span>
                            <span className="px-3 py-1.5 dark:bg-slate-800 bg-slate-100 rounded-lg dark:text-slate-300 text-slate-600">
                                <Users2 className="w-4 h-4 inline mr-1.5" />
                                {groups.length} groups
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Tabs */}
            <div className="px-6 py-4 border-b dark:border-slate-800 border-slate-200">
                <div className="max-w-5xl mx-auto flex gap-2">
                    <button
                        onClick={() => setActiveSection("groups")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeSection === "groups"
                                ? "bg-teal-600 text-white"
                                : "dark:text-slate-400 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`}
                    >
                        <Users2 className="w-4 h-4 inline mr-2" />
                        Groups
                    </button>
                    <button
                        onClick={() => setActiveSection("members")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeSection === "members"
                                ? "bg-teal-600 text-white"
                                : "dark:text-slate-400 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`}
                    >
                        <Users className="w-4 h-4 inline mr-2" />
                        Members
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="px-6 py-4">
                    <div className="max-w-5xl mx-auto">
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="px-6 py-6">
                <div className="max-w-5xl mx-auto">
                    {activeSection === "groups" && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold dark:text-white text-slate-900">Groups</h3>
                                    <p className="text-sm dark:text-slate-400 text-slate-500">
                                        {groups.length} of {limits.groupsPerWorkspace} groups
                                    </p>
                                </div>
                                <button
                                    onClick={() => setCreateModalOpen(true)}
                                    disabled={!canCreateGroup}
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    New Group
                                </button>
                            </div>

                            {/* Permission Warning */}
                            {!canEdit && (
                                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                                    <AlertCircle className="w-4 h-4" />
                                    Only organization <strong className="mx-1">admins</strong> and <strong className="mx-1">owners</strong> can manage groups.
                                </div>
                            )}

                            {/* Limit Warning */}
                            {canEdit && isLimitReached(groups.length, limits.groupsPerWorkspace) && (
                                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                                    <AlertCircle className="w-4 h-4" />
                                    Group limit reached for this workspace.
                                </div>
                            )}

                            {groups.length === 0 ? (
                                <div className="text-center py-12 dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl">
                                    <Users2 className="w-12 h-12 mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                                    <p className="dark:text-slate-400 text-slate-500">No groups yet</p>
                                    {canCreateGroup && (
                                        <button
                                            onClick={() => setCreateModalOpen(true)}
                                            className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
                                        >
                                            Create your first group
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {groups.map((group, index) => (
                                        <GroupCard
                                            key={group.id}
                                            group={group}
                                            index={index}
                                            onSelect={onGroupSelect}
                                            onDelete={openDeleteModal}
                                            canEdit={canEdit}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === "members" && (
                        <MembersList
                            organization={organization}
                            workspace={workspace}
                            members={members}
                            limits={limits}
                            userEmail={userEmail}
                            onRefresh={loadData}
                            showInviteForm={false}
                            context="workspace"
                        />
                    )}
                </div>
            </div>

            {/* Create Group Modal */}
            {createModalOpen && (
                <CreateEntityModal
                    type="group"
                    onClose={() => setCreateModalOpen(false)}
                    onSubmit={handleCreateGroup}
                    organization={organization}
                    workspace={workspace}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">Delete Group?</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2">
                                    Are you sure you want to delete <span className="font-semibold dark:text-white text-slate-900">{groupToDelete?.name}</span>?
                                    All member assignments will be removed.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => {
                                        setDeleteModalOpen(false);
                                        setGroupToDelete(null);
                                    }}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteGroup}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Delete"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkspaceDetail;
